import React, { useState, useEffect } from 'react';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, increment } from 'firebase/firestore';
import { db } from './firebaseConfig';
import './Trivia.css';
import logo from './assets/icons/logo-no-background.png';
import coffeeIcon from './assets/icons/bmc-logo-yellow.png';
import facebookIcon from './assets/icons/facebook-brands-solid.svg';
import xIcon from './assets/icons/x-twitter-brands-solid.svg';
import linkIcon from './assets/icons/copy-regular.svg';
import statsIcon from './assets/icons/chart-simple-solid.svg';
import triviaIcon from './assets/icons/masks-theater-solid.svg';
import thumbsupIcon from './assets/icons/thumbs-up-solid.svg';
import thumbsdownIcon from './assets/icons/thumbs-down-solid.svg';
import movieIcon from './assets/icons/film-solid.svg';
import { format } from 'date-fns';
import SideMenu from './SideMenu';
import FooterLinks from './FooterLinks';

const Trivia = ({ handleTwitterShare, handleFacebookShare, handleCopyLink, copied }) => {
	const [trivia, setTrivia] = useState(null);
	const [voted, setVoted] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [selectedVote, setSelectedVote] = useState(null);

	const navigate = useNavigate();
	const auth = getAuth();

	// Run sign-in and auth state listener once on mount
	useEffect(() => {
		signInAnonymously(auth).catch((error) => {
			console.error('Error signing in anonymously:', error);
		});

		const unsubscribe = onAuthStateChanged(auth, (user) => {
			// Auth state handling can be extended if needed.
		});

		return () => unsubscribe();
	}, [auth]);

	useEffect(() => {
		const fetchRandomTrivia = async () => {
			try {
				// Get *all* trivia docs.
				const triviaRef = collection(db, 'trivia');
				const triviaSnapshot = await getDocs(triviaRef);

				if (triviaSnapshot.empty) {
					console.error('No trivia found in the "trivia" collection.');
					return;
				}

				// Map over the snapshot to get an array of trivia objects.
				const allTrivia = triviaSnapshot.docs.map((doc) => ({
					id: doc.id,
					upvotes: doc.data().upvotes || 0,
					downvotes: doc.data().downvotes || 0,
					...doc.data(),
				}));

				// Pick a random trivia from the array.
				const randomIndex = Math.floor(Math.random() * allTrivia.length);
				const randomTrivia = allTrivia[randomIndex];

				setTrivia(randomTrivia);
			} catch (error) {
				console.error('Error fetching random trivia:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchRandomTrivia();
	}, []);

	useEffect(() => {
		if (trivia?.id) {
			const today = format(new Date(), 'yyyy-MM-dd');
			const lastTriviaVoteDate = localStorage.getItem('lastTriviaVoteDate');
			setVoted(lastTriviaVoteDate === today);
		}
	}, [trivia]);

	const handleVote = async (value) => {
		const today = format(new Date(), 'yyyy-MM-dd');
		const lastTriviaVoteDate = localStorage.getItem('lastTriviaVoteDate');

		if (lastTriviaVoteDate === today) {
			return;
		}

		if (!voted && trivia?.id && auth.currentUser) {
			try {
				const triviaRef = doc(db, 'trivia', trivia.id);
				const fieldToUpdate = value === 1 ? 'upvotes' : 'downvotes';

				await updateDoc(triviaRef, {
					[fieldToUpdate]: increment(1),
				});

				setTrivia((prevTrivia) => ({
					...prevTrivia,
					[fieldToUpdate]: prevTrivia[fieldToUpdate] + 1,
				}));

				localStorage.setItem('lastTriviaVoteDate', today);
				setSelectedVote(value);
				setVoted(true);
			} catch (error) {
				console.error('Error updating vote:', error);
			}
		}
	};

	if (isLoading) {
		return (
			<div className="loading-container">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div className="trivia-page">
			<div className="header-container">
				<div className="header-left">
					<SideMenu />
					<img
						src={triviaIcon}
						alt="Movie Quotes Guesser Trivia Icon"
						className="trivia-icon"
						onClick={() => navigate('/trivia')}
					/>
				</div>
				<div
					className="logo-box"
					onClick={() => {
						navigate('/');
					}}
				>
					<img src={logo} alt="Movie Quotes Guesser Logo" className="logo" />
					<h3 className="logo-text">Movie Quotes Guesser</h3>
				</div>
				<div className="header-right">
					<img src={statsIcon} alt="Stats Icon" className="stats-icon" onClick={() => navigate('/stats')} />
				</div>
			</div>

			{/* Trivia Section */}
			<div className="trivia-container">
				<div className="trivia-title-section">
					<img src={movieIcon} className="trivia-title-icon" alt="Movie Icon" />
					<h1 className="trivia-title-text">Trivia of the Day</h1>
					<img src={movieIcon} className="trivia-title-icon" alt="Movie Icon" />
				</div>
				<div className="trivia-image-container">
					<img src={trivia.imgURL} alt="Trivia" className="trivia-image" />
				</div>
				<p className="trivia-text">{trivia.text}</p>

				{/* Vote Buttons */}
				<div className="vote-buttons">
					<div
						className={`vote-box ${voted ? (selectedVote === 1 ? 'voted' : 'muted') : ''}`}
						onClick={() => {
							if (!voted) handleVote(1);
						}}
					>
						<span className="vote-label">Interesting</span>
						<img src={thumbsupIcon} alt="Up vote icon" />
						<span className="vote-count">{trivia.upvotes}</span>
					</div>
					<div
						className={`vote-box ${voted ? (selectedVote === -1 ? 'voted' : 'muted') : ''}`}
						onClick={() => {
							if (!voted) handleVote(-1);
						}}
					>
						<span className="vote-label">Meh</span>
						<img src={thumbsdownIcon} alt="Down vote icon" />
						<span className="vote-count">{trivia.downvotes}</span>
					</div>
				</div>

				{/* Go Home Button */}
				<div className="go-home-button-container">
					<button className="go-home-button" onClick={() => navigate('/')}>
						← Home
					</button>
				</div>
			</div>

			{/* Footer Section */}
			<div className="footer-container">
				<div className="share-buttons">
					<img src={xIcon} alt="Share us on X" className="share-image" onClick={handleTwitterShare} />
					<img
						src={facebookIcon}
						alt="Share us on Facebook"
						className="share-image"
						onClick={handleFacebookShare}
					/>
					<img src={linkIcon} alt="Copy our website link" className="share-image" onClick={handleCopyLink} />
					{copied && <span className="copied-text">Copied</span>}
				</div>
				<FooterLinks />
				<a
					href="https://buymeacoffee.com/moviequotesguesser"
					target="_blank"
					rel="noopener noreferrer"
					className="support-button"
				>
					<img src={coffeeIcon} alt="Buy Me A Coffee" className="support-image" />
				</a>
			</div>
		</div>
	);
};

export default Trivia;
